import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Send } from "lucide-react";

const ELIGIBILITY_PLACEHOLDER =
  "Décrivez ici (ex. type de client, zone, niveau de revenu, conformité souhaitée, délai de rappel, etc.)";

const QUESTION_PLACEHOLDER =
  "Ex. fonctionnement d’un compte, facturation, abonnement, partenariat, une idée… Soyez aussi bref que vous voulez.";

const BUG_PLACEHOLDER =
  "Décrivez ce qui ne va pas : page concernée, ce que vous faisiez, ce que vous attendiez, navigateur / mobile si utile…";

const PLACEHOLDER_PRODUIT = "Ex. assurance pro, CRM, mutuelle, isolation… (plusieurs idées possibles, séparées par des virgules)";

/** Suggestion de produit, question libre ou signalement de bug (état interne : question | bug | produit). */
export default function ProductSuggestionForm({ context = "unknown", className = "", onSubmitted }) {
  const { user } = useAuth();
  const isDashboard = context === "dashboard" || context === "navbar" || context === "member-shell";
  const [loading, setLoading] = useState(false);
  /** Jamais prérempli depuis la session : même compte sur plusieurs postes / partage d’écran, l’utilisateur saisit l’e-mail voulu. */
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState("question");
  const [suggestion, setSuggestion] = useState("");
  const [message, setMessage] = useState("");

  const isProduit = kind === "produit";
  const isMessageKind = kind === "question" || kind === "bug";
  const showEmailField = !isDashboard || !user?.email;

  const submit = async (e) => {
    e.preventDefault();
    if (isMessageKind) {
      if (!String(message).trim()) {
        toast.error(kind === "bug" ? "Décrivez le bug (même brièvement)." : "Indiquez votre message ou question.");
        return;
      }
    } else if (!suggestion.trim()) {
      toast.error("Indiquez le produit ou le service proposé.");
      return;
    }
    setLoading(true);
    try {
      const resolvedEmail = (
        showEmailField ? (email || "").trim() : (email || user?.email || "").trim()
      ) || null;
      const payload = {
        context,
        user_id: user?.user_id || user?.id || null,
        email: resolvedEmail,
        suggested_products: isProduit ? suggestion.trim() : "—",
        suggestion_type: kind,
        message: (message || "").trim() || null,
      };
      const { error } = await supabase.from("product_suggestions").insert(payload);
      if (error) throw error;
      toast.success("Merci, on revient vers vous rapidement.");
      setSuggestion("");
      setMessage("");
      if (typeof onSubmitted === "function") onSubmitted();
    } catch (err) {
      toast.error(err.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const emailOk = !showEmailField || String(email).trim().length > 0;
  const canSubmit = loading
    ? false
      : !emailOk
      ? false
      : isMessageKind
        ? String(message).trim().length > 0
        : suggestion.trim().length > 0;

  const compactLabel = isDashboard ? "text-xs font-medium text-slate-600" : "";

  return (
    <form onSubmit={submit} className={cn(isDashboard && "max-w-full", className)} data-testid={`product-suggestion-form-${context}`}>
      <div className={cn(isDashboard ? "space-y-4" : "space-y-4")}>
        <p className="text-xs text-slate-500 -mt-1 sm:text-sm">
          Choisissez le type d’échange : <strong>question</strong> générale, <strong>bug</strong> (quelque chose ne fonctionne pas),
          ou <strong>nouveau produit ou service</strong> à ajouter sur RdvMarket.
        </p>

        <div
          className={cn(
            "gap-3",
            showEmailField ? "grid sm:grid-cols-2" : "flex flex-col sm:flex-row sm:items-end sm:gap-4"
          )}
        >
          {showEmailField && (
            <div className="min-w-0 flex-1 space-y-1.5 sm:min-w-0">
              <Label htmlFor={`ps-email-${context}`} className={compactLabel} required>
                E-mail
              </Label>
              <Input
                id={`ps-email-${context}`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cn("h-10", isDashboard && "bg-white")}
                placeholder="vous@exemple.com"
                required
              />
            </div>
          )}
          <div
            className={cn(
              "min-w-0 space-y-1.5",
              !showEmailField && isProduit && "sm:w-44 sm:shrink-0",
              !showEmailField && isMessageKind && "sm:max-w-xs sm:shrink-0",
            )}
          >
            <Label className={compactLabel}>Type d’échange</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className={cn("h-10", isDashboard && "bg-white")} data-testid={`ps-kind-${context}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="question">Question / message</SelectItem>
                <SelectItem value="bug">Bug ou dysfonctionnement</SelectItem>
                <SelectItem value="produit">Suggérer un produit ou un service</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!showEmailField && isProduit && (
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor={`ps-value-${context}`} className="text-xs font-medium text-slate-600" required>
                Produit ou service
              </Label>
              <Input
                id={`ps-value-${context}`}
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                className="h-10 bg-white"
                placeholder="Ex. assurance pro, CRM…"
                required
              />
            </div>
          )}
        </div>

        {isMessageKind && (
          <div className="space-y-1.5">
            <Label htmlFor={`ps-question-body-${context}`} className={cn(compactLabel || "text-sm font-medium")}>
              {kind === "bug" ? "Description du bug" : "Votre message"}
            </Label>
            <Textarea
              id={`ps-question-body-${context}`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={isDashboard ? 4 : 5}
              className={cn("resize-y bg-white text-sm min-h-[7.5rem]", isDashboard && "min-h-[7rem]")}
              placeholder={kind === "bug" ? BUG_PLACEHOLDER : QUESTION_PLACEHOLDER}
              data-testid={`ps-message-${context}`}
              required
            />
            <p className="text-[11px] text-slate-500 sm:text-xs">
              {kind === "bug" ? (
                <>Indiquez l’écran ou l’action concernée si vous pouvez ; ça aide à reproduire le problème.</>
              ) : (
                <>
                  Une seule zone : question simple, partenariat, compte, ou tout autre sujet. Pas besoin de détailler un
                  produit ici.
                </>
              )}
            </p>
          </div>
        )}

        {isProduit && showEmailField && (
          <div className="space-y-1.5">
            <Label htmlFor={`ps-value-${context}`} required>
              Produit / service suggéré(s)
            </Label>
            <Input
              id={`ps-value-${context}`}
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              className="h-11"
              placeholder={PLACEHOLDER_PRODUIT}
              required
            />
            <p className="text-[11px] text-slate-500">Plusieurs idées possibles, séparées par des virgules.</p>
          </div>
        )}

        {isProduit && !showEmailField && isDashboard && (
          <p className="-mt-1 text-[11px] text-slate-500">Plusieurs idées possibles, séparées par des virgules.</p>
        )}

        {isProduit && (
          <div className="space-y-1.5 rounded-xl border border-slate-200/90 bg-slate-50/50 p-4 sm:p-5">
            <Label htmlFor={`ps-criteria-${context}`} className="text-sm font-semibold leading-snug text-[#0B0D12] sm:text-base">
              Critères d’éligibilité des dossiers sur cette thématique
              <span className="font-normal text-slate-500"> (optionnel)</span>
            </Label>
            <p className="text-xs text-slate-500 sm:text-sm">
              Si vous le souhaitez, précisez ce qui compte pour vous (cible, zone, budget, conformité, etc.) pour les
              annonces de ce type.
            </p>
            <Textarea
              id={`ps-criteria-${context}`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={isDashboard ? 4 : 5}
              className={cn("mt-1 resize-y bg-white text-sm", isDashboard && "min-h-[6rem]")}
              placeholder={ELIGIBILITY_PLACEHOLDER}
            />
          </div>
        )}

        <div className={cn("flex pt-1", isDashboard ? "justify-center" : "")}>
          <Button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              "btn-primary h-10 rounded-lg px-5 font-semibold shadow-sm shadow-[#2D7FF9]/20",
              isDashboard ? "inline-flex w-full min-w-[9rem] items-center justify-center gap-2 sm:w-auto" : "h-11 rounded-md"
            )}
            data-testid={`product-suggestion-submit-${context}`}
          >
            <Send className="h-4 w-4 opacity-90" aria-hidden />
            {loading ? "Envoi…" : "Envoyer"}
          </Button>
        </div>
      </div>
    </form>
  );
}
